import { EraQlAst, Call, Closure, AssocEntry } from '../../eraql-ast/ast';
import { Assert, assertExists } from '@cotera/utilities';
import _ from 'lodash';

import { WithMeta } from '../../lex';

/**
 * Returns whether both asts are the same, including comments (but ignoring token ranges).
 */
export function sameEraQLAst(
  a: WithMeta<EraQlAst>,
  b: WithMeta<EraQlAst>
): boolean {
  const [aAst, { comments: aComments }] = a;
  const [bAst, { comments: bComments }] = b;

  if (!_.isEqual(aComments, bComments)) {
    return false;
  }

  switch (aAst.t) {
    case 'symbol':
    case 'literal':
    case 'ident': {
      const _1: 'symbol' | 'literal' | 'ident' = aAst.t; // assert type
      return _.isEqual(aAst, bAst);
    }

    case 'call': {
      if (bAst.t !== 'call') {
        return false;
      }

      if (aAst.display !== bAst.display) {
        return false;
      } // Actually, generated eraQL may change the display (ie to pipes only)

      return (
        sameCallOp(aAst.op, bAst.op) &&
        sameList(aAst.args, bAst.args, sameEraQLAst)
      );
    }

    case 'assoc':
      return (
        bAst.t === 'assoc' &&
        sameList(aAst.entries, bAst.entries, sameAssocEntry)
      );

    case 'list':
      return (
        bAst.t === 'list' && sameList(aAst.elems, bAst.elems, sameEraQLAst)
      );

    case 'let':
      return (
        bAst.t === 'let' &&
        sameData(aAst.sym, bAst.sym, _.isEqual) &&
        sameEraQLAst(aAst.bindingExpr, bAst.bindingExpr) &&
        sameEraQLAst(aAst.body, bAst.body)
      );

    case 'attr-access':
      return (
        bAst.t === 'attr-access' &&
        sameEraQLAst(aAst.in, bAst.in) &&
        sameData(aAst.attr, bAst.attr, _.isEqual)
      );

    case 'closure':
      return (
        bAst.t === 'closure' &&
        sameEraQLAst(aAst.body, bAst.body) &&
        sameData(aAst.args, bAst.args, sameClosureArgs)
      );

    default:
      return Assert.unreachable(aAst);
  }
}

function sameList<T>(
  elemsA: readonly WithMeta<T>[],
  elemsB: readonly WithMeta<T>[],
  sameT: (elemA: WithMeta<T>, elemB: WithMeta<T>) => boolean
): boolean {
  if (elemsA.length !== elemsB.length) {
    return false;
  }

  return elemsA.every((elemA, idx) => {
    const elemB = assertExists(elemsB[idx]);
    return sameT(elemA, elemB);
  });
}

function sameData<T>(
  withMetaA: WithMeta<T>,
  withMetaB: WithMeta<T>,
  sameT: (a: T, b: T) => boolean
): boolean {
  const [dataA, { comments: commentsA }] = withMetaA;
  const [dataB, { comments: commentsB }] = withMetaB;

  return _.isEqual(commentsA, commentsB) && sameT(dataA, dataB);
}

function sameCallOp(opA: Call['op'], opB: Call['op']): boolean {
  const [dataA, metaA] = opA;
  const [dataB, metaB] = opA;

  switch (dataA.t) {
    case 'tag':
    case 'operator':
      return sameData(opA, opB, _.isEqual);
    default: {
      const _1: EraQlAst['t'] = dataA.t; // Assert type
      return (
        dataB.t === dataA.t && sameEraQLAst([dataA, metaA], [dataB, metaB])
      );
    }
  }
}

const sameAssocEntry = (
  entryAWithMeta: WithMeta<AssocEntry>,
  entryBWithMeta: WithMeta<AssocEntry>
): boolean =>
  sameData(entryAWithMeta, entryBWithMeta, (entryA, entryB) => {
    switch (entryA.t) {
      case 'assoc-kv': {
        if (entryB.t !== 'assoc-kv') {
          return false;
        }
        if (!sameData(entryA.key, entryB.key, _.isEqual)) {
          return false;
        }
        return sameEraQLAst(entryA.val, entryB.val);
      }

      case 'assoc-spread': {
        if (entryB.t !== 'assoc-spread') {
          return false;
        }
        return sameEraQLAst(entryA.val, entryB.val);
      }

      default:
        return Assert.unreachable(entryA);
    }
  });

type ClosureArgs = Closure['args'] extends WithMeta<infer T> ? T : never;

function sameClosureArgs(argsA: ClosureArgs, argsB: ClosureArgs): boolean {
  const symsA = argsA.map(({ sym }) => sym);
  const symsB = argsB.map(({ sym }) => sym);

  return sameList(symsA, symsB, (symA, symB) =>
    sameData(symA, symB, _.isEqual)
  );
}
